import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Package, RefreshCw, CheckCircle2, AlertCircle, Clock, KeyRound,
  Search, Check, Layers, Eye, Upload, X, ExternalLink, Image as ImageIcon,
  Zap, FileText,
} from "lucide-react";
import {
  adminSanmarStatus, adminSanmarSync, adminSanmarStyles,
  adminSanmarSetApproval, adminSanmarRuns, adminSanmarClearErrors,
  adminSanmarGetSettings, adminSanmarPutSettings,
  adminSanmarPreview, adminSanmarPromote, adminSanmarPromoteAll,
  adminSanmarScrapeBacks, adminSanmarScrapeStatus, adminSanmarScrapeRuns,
  adminSanmarSyncLiveListings, adminSanmarSyncLiveStatus,
  adminCategoriesTaxonomy, adminCategoriesClassify,
  adminSanmarScrapeSpecs, adminSanmarScrapeSpecsStatus,
  adminSanmarGetMode, adminSanmarSetMode,
} from "../../lib/api";
import CategoryPicker from "./CategoryPicker";
import useSyncSpeed from "../../hooks/useSyncSpeed";

/**
 * Admin → SanMar Bulk Data Sync (iter57 — Phase 1).
 *
 * Staging-only. Nothing here writes to db.products. Once you approve a
 * style, a follow-up feature (Phase 2, coming soon) will promote it
 * into the live customizable catalog while preserving your existing
 * manual products.
 */

const BRAND_QUICK_PICKS = ["ATC", "PORT AUTHORITY", "OGIO", "GILDAN", "DISTRICT", "NIKE"];

// iter81 — desktop notification helper. Fires a native browser notification
// when a long-running sync finishes so the admin can close the tab / walk
// away and still get pinged. Silently no-ops if the browser blocks
// notifications or the user denied permission.
function notifyDesktop(title, body) {
  try {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    // Don't ping if the user is actively looking at the tab.
    if (typeof document !== "undefined" && document.visibilityState === "visible") return;
    const n = new Notification(title, {
      body,
      icon: "/favicon.ico",
      tag: "sanmar-sync",       // collapses repeats into one popup
      renotify: true,
    });
    // Focus the tab on click.
    n.onclick = () => { try { window.focus(); n.close(); } catch { /* noop */ } };
  } catch {
    /* noop — never let a notif failure break the sync flow */
  }
}

async function requestDesktopNotifyPermission() {
  try {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "default") {
      await Notification.requestPermission();
    }
  } catch { /* noop */ }
}

// iter88 — 12-hour time formatters with AM/PM. Every timestamp shown in
// this tab MUST go through one of these so the operator can tell at a
// glance when the last sync ran — no more decoding 24-hour "16:24:48"
// values.
function fmt12hrTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true,
    });
  } catch { return "—"; }
}
function fmt12hrDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true,
    });
  } catch { return "—"; }
}
function fmt12hrDateTimeShort(iso) {
  // Compact form for row headers: "Jul 10, 4:24:48 PM"
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "2-digit",
      hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true,
    });
  } catch { return "—"; }
}

export default function SanmarSyncTab({ pw }) {
  const [status, setStatus] = useState(null);
  // iter76c — default the manual admin sync to `production` too (per
  // operator request 2026-07-06). Sandbox is preserved as an explicit
  // fallback in the dropdown for troubleshooting.
  const [mode, setMode] = useState("production");
  const [dryRun, setDryRun] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [pollTimer, setPollTimer] = useState(null);
  const [brandFilter, setBrandFilter] = useState("ATC");
  const [approvedFilter, setApprovedFilter] = useState("");   // "" | "true" | "false"
  const [search, setSearch] = useState("");
  const [styles, setStyles] = useState([]);
  const [runs, setRuns] = useState([]);
  // Phase 2 state
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [previewStyle, setPreviewStyle] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [promotingStyle, setPromotingStyle] = useState("");
  const [promotingAll, setPromotingAll] = useState(false);
  const [forceOverwrite, setForceOverwrite] = useState(false);
  // iter60 — back-image scraper state
  const [scrapeStatus, setScrapeStatus] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [scrapePollTimer, setScrapePollTimer] = useState(null);
  const [scrapeOnlyApproved, setScrapeOnlyApproved] = useState(true);
  const [scrapeForce, setScrapeForce] = useState(false);
  const [scrapeRuns, setScrapeRuns] = useState([]);
  // iter61/62 — one-click reconcile of live SanMar listings (now background job)
  const [reconciling, setReconciling] = useState(false);
  const [lastReconcile, setLastReconcile] = useState(null);
  const [reconcilePollTimer, setReconcilePollTimer] = useState(null);
  // iter66 — category picker state
  const [taxonomy, setTaxonomy] = useState(null);
  const [bulkCategory, setBulkCategory] = useState({ category: "", subcategory: "" });
  const [previewInferred, setPreviewInferred] = useState(null);
  const [previewCategory, setPreviewCategory] = useState({ category: "", subcategory: "" });
  // iter79d — DB-backed mode override state
  const [modeInfo, setModeInfo] = useState(null);
  const [savingMode, setSavingMode] = useState(false);

  const refreshStatus = useCallback(async () => {
    try {
      const s = await adminSanmarStatus(pw);
      setStatus(s);
      setMode((prev) => prev || s.mode_env || "sandbox");
      setRuns(await adminSanmarRuns(pw, 10));
      setSettings(await adminSanmarGetSettings(pw));
      setScrapeStatus(await adminSanmarScrapeStatus(pw));
      setScrapeRuns(await adminSanmarScrapeRuns(pw, 10));
      // iter79d — pull the effective mode (may be DB override or env fallback)
      try { setModeInfo(await adminSanmarGetMode(pw)); } catch { /* noop */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load SanMar status");
    }
  }, [pw]);

  const refreshStyles = useCallback(async () => {
    try {
      const approvedFlag =
        approvedFilter === "true" ? true : approvedFilter === "false" ? false : undefined;
      const rows = await adminSanmarStyles(pw, {
        brand: brandFilter || undefined,
        approved: approvedFlag,
        q: search || undefined,
        limit: 200,
      });
      setStyles(rows);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load styles");
    }
  }, [pw, brandFilter, approvedFilter, search]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);
  useEffect(() => { refreshStyles(); }, [refreshStyles]);

  // iter71/81 — if a sync is already running when the tab mounts (user
  // reloaded the page or navigated away), resume polling automatically
  // so they don't have to click "SYNC NOW" again to see completion.
  // Recognises every "not yet finished" phase: starting → running →
  // started → in_progress. The `starting` state is written before the
  // SanMar HTTP fetch so it survives page reloads mid-fetch (~90s window).
  useEffect(() => {
    if (!status || syncing) return;
    const lr = status.last_run || {};
    const runningPhases = new Set(["starting", "running", "started", "in_progress"]);
    const stillRunning = lr.run_id && runningPhases.has(lr.phase) && !lr.finished_at;
    if (stillRunning) {
      setSyncing(true);
      _pollUntilDone(lr.run_id, !!lr.dry_run);
    }
  }, [status?.last_run?.run_id, status?.last_run?.phase]);

  // iter81 — background heartbeat: poll status every 10s while the tab
  // is mounted even if the user isn't actively watching a sync. This
  // guarantees we detect a still-running sync on tab reactivation or
  // if the initial `refreshStatus()` ran before the backend persisted
  // the "starting" record.
  useEffect(() => {
    const hb = setInterval(() => { refreshStatus(); }, 10000);
    return () => clearInterval(hb);
  }, [refreshStatus]);
  // Load taxonomy once on mount for the category picker dropdowns
  useEffect(() => {
    (async () => {
      try { setTaxonomy(await adminCategoriesTaxonomy(pw)); }
      catch (_e) { /* ignore */ }
    })();
  }, [pw]);
  useEffect(() => () => pollTimer && clearInterval(pollTimer), [pollTimer]);
  useEffect(() => () => scrapePollTimer && clearInterval(scrapePollTimer), [scrapePollTimer]);
  useEffect(() => () => reconcilePollTimer && clearInterval(reconcilePollTimer), [reconcilePollTimer]);
  // Rehydrate last reconcile on tab mount
  useEffect(() => {
    (async () => {
      try {
        const s = await adminSanmarSyncLiveStatus(pw);
        if (s && s.phase && s.phase !== "idle") setLastReconcile(s);
      } catch (_e) { /* ignore */ }
    })();
  }, [pw]);

  const runScrapeBacks = async () => {
    if (scraping) return;
    setScraping(true);
    requestDesktopNotifyPermission();
    try {
      const r = await adminSanmarScrapeBacks(pw, {
        only_approved: scrapeOnlyApproved,
        force: scrapeForce,
      });
      if (!r.ok) throw new Error(r.error || "failed to queue");
      const targetId = r.run_id;
      toast.info(`Back-image scrape queued. Polling every 5s…`);
      const t = setInterval(async () => {
        const s = await adminSanmarScrapeStatus(pw);
        setScrapeStatus(s);
        if (s?.last_run?.run_id === targetId && s.last_run.finished_at) {
          clearInterval(t); setScrapePollTimer(null); setScraping(false);
          if (s.last_run.ok) {
            toast.success(
              `Back-image scrape complete — ${s.last_run.with_back}/${s.last_run.total_styles} styles have back images · ${s.last_run.rows_updated} variations updated`
            );
            notifyDesktop(
              "Back-image scrape complete",
              `${s.last_run.with_back}/${s.last_run.total_styles} styles matched · ${s.last_run.rows_updated} variations updated`
            );
          } else {
            toast.error(`Scrape had issues: ${(s.last_run.errors || [])[0] || "see run log"}`);
            notifyDesktop("Back-image scrape failed", (s.last_run.errors || [])[0] || "see run log");
          }
          setScrapeRuns(await adminSanmarScrapeRuns(pw, 10));
        }
      }, 5000);
      setScrapePollTimer(t);
      // Safety: stop polling after 15 min
      setTimeout(() => { clearInterval(t); setScrapePollTimer(null); setScraping(false); }, 900000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Scrape failed to queue");
      setScraping(false);
    }
  };

  const runReconcile = async () => {
    if (reconciling) return;
    const proceed = window.confirm(
      "This will:\n" +
      "  1. Link every SanMar-source product on your live site to the Bulk API staging data\n" +
      "  2. Auto-approve every matched style\n" +
      "  3. Scrape back-view images from sanmarcanada.com (can take 2-5 min on large catalogs)\n" +
      "  4. Refresh supplier_cost + inventory + backfill missing back_color_images\n" +
      "  5. Activate any inactive matched products\n\n" +
      "Runs in the background — you'll see live progress. Safe update path only, nothing gets replaced wholesale. Continue?"
    );
    if (!proceed) return;
    setReconciling(true);
    requestDesktopNotifyPermission();
    try {
      const kick = await adminSanmarSyncLiveListings(pw, {
        scrape_backs: true, force_overwrite: false, activate: true,
      });
      if (!kick?.ok) throw new Error(kick?.detail || "failed to queue");
      const targetRunId = kick.run_id;
      toast.info("Reconcile queued. Polling every 4s…");
      const t = setInterval(async () => {
        try {
          const s = await adminSanmarSyncLiveStatus(pw);
          if (!s) return;
          setLastReconcile(s);
          if (s.run_id !== targetRunId) return;  // still on the previous run
          if (s.phase === "done" || s.phase === "failed") {
            clearInterval(t); setReconcilePollTimer(null); setReconciling(false);
            if (s.phase === "failed") {
              toast.error(`Reconcile failed: ${s.error || "unknown error"}`);
              notifyDesktop("SanMar reconcile failed", s.error || "unknown error");
              return;
            }
            const p = s.promote || {};
            const scr = s.scrape || {};
            toast.success(
              `Reconciled ${(s.matched_styles || []).length} styles · ` +
              `${s.backfilled_style_field} field backfills · ` +
              `${s.approved_variations} variations approved · ` +
              `${scr.with_back ?? 0}/${scr.total_styles ?? 0} back images · ` +
              `${(p.created || 0) + (p.updated || 0) + (p.overwritten || 0)} refreshed · ` +
              `${s.activated_products} activated`
            );
            notifyDesktop(
              "SanMar reconcile complete",
              `${(s.matched_styles || []).length} styles matched · ${s.approved_variations} variations approved · ${s.activated_products} products activated`
            );
            refreshStyles();
            refreshStatus();
          }
        } catch (pollErr) {
          // Non-fatal — keep polling
          console.warn("reconcile poll error", pollErr);
        }
      }, 4000);
      setReconcilePollTimer(t);
      // Safety kill after 15 min
      setTimeout(() => {
        clearInterval(t); setReconcilePollTimer(null); setReconciling(false);
      }, 900000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed to start reconcile");
      setReconciling(false);
    }
  };


  // iter71 — poll status every 6s until phase === "done" or "failed".
  // Prior versions matched on run_id alone, which now trips the moment
  // the backend writes the first in_progress snapshot (created after
  // the first chunk flush) → partial counts + polling stopped mid-sync.
  const _pollUntilDone = useCallback((targetId, dryRun) => {
    const t = setInterval(async () => {
      try {
        const s = await adminSanmarStatus(pw);
        setStatus(s);
        const lr = s?.last_run || {};
        if (lr.run_id !== targetId) return;   // still on a previous run
        const phase = lr.phase;
        // Backwards-compat: legacy runs before iter71 had no `phase` field
        // and were persisted only once, at completion — treat presence of
        // `finished_at` alone as "done" for those.
        const done = phase === "done" || phase === "failed" || (!phase && lr.finished_at);
        if (!done) return;
        clearInterval(t); setPollTimer(null); setSyncing(false);
        if (lr.ok) {
          toast.success(
            `Sync ${dryRun ? "(dry-run) " : ""}complete — fetched ${lr.fetched}, ${lr.created} new, ${lr.updated} updated`
          );
          notifyDesktop(
            `SanMar sync complete${dryRun ? " (dry-run)" : ""}`,
            `Fetched ${lr.fetched?.toLocaleString?.() || lr.fetched} · ${lr.created} new · ${lr.updated} updated · ${lr.styles} styles`
          );
        } else {
          toast.error(`Sync failed: ${(lr.errors || [])[0] || "unknown"}`);
          notifyDesktop("SanMar sync failed", (lr.errors || [])[0] || "unknown error");
        }
        refreshStyles();
        setRuns(await adminSanmarRuns(pw, 10));
      } catch (pollErr) {
        // Non-fatal — keep polling. Kubernetes ingress occasionally 502s
        // during long syncs; the next poll usually recovers.
        console.warn("sanmar sync poll error", pollErr);
      }
    }, 6000);
    setPollTimer(t);
    // Safety kill after 30 min — 20K+ variations via safe_bulk_write on a
    // flaky Atlas can legitimately take 10-20 min end-to-end.
    setTimeout(() => { clearInterval(t); setPollTimer(null); setSyncing(false); }, 1_800_000);
  }, [pw, refreshStyles]);

  const runSync = async () => {
    if (syncing) return;
    setSyncing(true);
    // Ask for desktop notification permission on first user gesture so
    // the browser accepts the prompt (background prompts get suppressed).
    requestDesktopNotifyPermission();
    try {
      const r = await adminSanmarSync(pw, { mode, dryRun });
      if (r.ok) {
        toast.info(`Sync queued (${mode}${dryRun ? " · dry-run" : ""}). Polling every 6s…`);
        _pollUntilDone(r.run_id, dryRun);
      } else {
        toast.error(r.error || "Sync failed to queue");
        setSyncing(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
      setSyncing(false);
    }
  };

  const toggleApproval = async (style, current) => {
    try {
      await adminSanmarSetApproval(pw, style, !current);
      toast.success(`${style} ${current ? "unapproved" : "approved"}`);
      refreshStyles();
      refreshStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update approval");
    }
  };

  const saveSettings = async (patch) => {
    setSavingSettings(true);
    try {
      const r = await adminSanmarPutSettings(pw, patch);
      setSettings(r);
      toast.success("Promotion settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingSettings(false);
    }
  };

  const openPreview = async (style) => {
    setPreviewStyle(style);
    setPreviewData(null);
    setPreviewLoading(true);
    setPreviewInferred(null);
    setPreviewCategory({ category: "", subcategory: "" });
    try {
      setPreviewData(await adminSanmarPreview(pw, style));
      // Fetch the classifier's suggestion for this style so the Category
      // Picker in the modal shows the ✨ chip + auto-fills.
      try {
        const inf = await adminCategoriesClassify(pw, { style });
        setPreviewInferred({ category: inf.category, subcategory: inf.subcategory });
      } catch (_e) { /* non-fatal */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
      setPreviewStyle(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewStyle(null); setPreviewData(null);
    setPreviewInferred(null);
    setPreviewCategory({ category: "", subcategory: "" });
  };

  const promoteOne = async (style) => {
    if (promotingStyle) return;
    setPromotingStyle(style);
    try {
      const r = await adminSanmarPromote(pw, style, forceOverwrite, {
        category: previewCategory.category,
        subcategory: previewCategory.subcategory,
      });
      toast.success(
        r.action === "created"
          ? `${style} created as ${r.slug} (draft — inactive) · category=${previewCategory.category || "auto"}`
          : r.action === "overwritten"
            ? `${style} overwritten — full SanMar data replaced live product`
            : `${style} updated — SanMar metadata refreshed`
      );
      closePreview();
      refreshStyles();
      refreshStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote failed");
    } finally {
      setPromotingStyle("");
    }
  };

  const promoteAll = async () => {
    const bulkNote = bulkCategory.category
      ? `\n\nEVERY promoted product will be set to category='${bulkCategory.category}' subcategory='${bulkCategory.subcategory || "adult"}'. Leave both empty to use per-style smart classifier.`
      : "\n\nCategory: using the per-style smart classifier (t-shirts, hoodies, hats, etc. auto-detected from name).";
    const warn = (forceOverwrite
      ? "FORCE OVERWRITE is ON — every existing SanMar-linked product will be REPLACED wholesale (images, name, colors, sizes, description). Only the id + created_at are preserved. Continue?"
      : "Promote every approved style to the live catalog? Existing manual products are preserved — only new draft products will be created."
    ) + bulkNote;
    if (!window.confirm(warn)) return;
    setPromotingAll(true);
    try {
      const r = await adminSanmarPromoteAll(pw, forceOverwrite, {
        category: bulkCategory.category,
        subcategory: bulkCategory.subcategory,
      });
      toast.success(`Promotion complete — created ${r.created}, updated ${r.updated}, overwritten ${r.overwritten || 0}, skipped ${r.skipped}`);
      refreshStyles();
      refreshStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote-all failed");
    } finally {
      setPromotingAll(false);
    }
  };

  if (!status) return <div className="font-mono text-xs text-zinc-500">LOADING…</div>;

  const configured = !!status.credentials_configured;
  const last = status.last_run || {};

  return (
    <div className="space-y-6" data-testid="sanmar-tab">
      <div>
        <h2 className="font-display text-2xl uppercase tracking-tight flex items-center gap-2">
          <Package size={22} className="text-[var(--tc-red)]" /> SanMar Bulk Data Sync
        </h2>
        <p className="font-body text-sm text-zinc-400 mt-1">
          Phase 1: <strong>staging only</strong>. Pulls the full SanMar catalog into
          a staging table. Nothing on your live site changes until you approve
          styles and (Phase 2) promote them to the customizable catalog.
        </p>
      </div>

      {/* iter81 — top-of-page prominent live sync indicator. Visible the
          moment the tab loads if a sync is running (survives reloads via
          DB-persisted phase). Fixes the "spinning wheel with no way to
          tell if it's working" complaint. */}
      {["starting", "running", "started", "in_progress"].includes(last.phase) && !last.finished_at && (
        <div
          className="border-2 border-blue-500 bg-blue-950/40 p-4 flex flex-wrap items-center justify-between gap-3 animate-pulse-slow"
          data-testid="sanmar-sync-live-banner"
        >
          <div className="flex items-center gap-3">
            <RefreshCw size={20} className="text-blue-300 animate-spin" />
            <div>
              <div className="font-display text-base uppercase tracking-widest text-blue-100">
                SanMar sync running in the background
              </div>
              <div className="font-mono text-[11px] text-blue-300 mt-0.5">
                {(() => {
                  const done = (last.created ?? 0) + (last.updated ?? 0);
                  const total = last.total_products_reported || last.fetched || 0;
                  if (["starting", "running"].includes(last.phase) || (!last.fetched && done === 0)) {
                    return "Contacting SanMar API… (can take up to 90s)";
                  }
                  if (done > 0 && total > 0) {
                    const pct = Math.min(100, Math.round((done / total) * 100));
                    return `${pct}% complete · ${done.toLocaleString()} / ${total.toLocaleString()} SKUs · safe to close this tab`;
                  }
                  return "Fetching product list from SanMar…";
                })()}
              </div>
            </div>
          </div>
          <div className="font-mono text-[10px] tracking-widest text-blue-400 text-right">
            RUN {last.run_id?.slice(-10) || "—"}<br />
            <span className="text-blue-500">
              started {fmt12hrTime(last.started_at)}
            </span>
          </div>
        </div>
      )}

      {/* Status cards */}
      <div className="grid md:grid-cols-4 gap-3">
        <div className={`border p-4 ${configured ? "border-emerald-900 bg-emerald-950/20" : "border-red-900 bg-red-950/20"}`}>
          <div className="flex items-center gap-2 mb-1">
            <KeyRound size={13} className={configured ? "text-emerald-400" : "text-red-400"} />
            <span className="font-mono text-[10px] tracking-widest text-zinc-500">CREDENTIALS</span>
          </div>
          <div className={`font-display text-xl ${configured ? "text-emerald-300" : "text-red-300"}`} data-testid="sanmar-key-status">
            {configured ? "CONFIGURED" : "MISSING"}
          </div>
        </div>
        <div className="border border-[#222] bg-black p-4">
          <div className="flex items-center gap-2 mb-1">
            <Layers size={13} className="text-zinc-500" />
            <span className="font-mono text-[10px] tracking-widest text-zinc-500">STAGED ROWS</span>
          </div>
          <div className="font-display text-3xl text-zinc-100" data-testid="sanmar-rows">{status.total_staged_rows}</div>
          <div className="font-mono text-[10px] text-zinc-500 mt-1">
            {status.distinct_styles} styles
          </div>
        </div>
        <div className="border border-[#222] bg-black p-4">
          <div className="flex items-center gap-2 mb-1">
            <Check size={13} className="text-emerald-400" />
            <span className="font-mono text-[10px] tracking-widest text-zinc-500">APPROVED</span>
          </div>
          <div className="font-display text-3xl text-emerald-300" data-testid="sanmar-approved">{status.approved_styles_count}</div>
          <div className="font-mono text-[10px] text-zinc-500 mt-1">
            {status.approved_variations} variations
          </div>
        </div>
        <div className="border border-[#222] bg-black p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={13} className="text-zinc-500" />
            <span className="font-mono text-[10px] tracking-widest text-zinc-500">LAST SYNC</span>
          </div>
          <div className="font-mono text-xs text-zinc-300" data-testid="sanmar-last-run">
            {last.started_at ? fmt12hrDateTime(last.started_at) : "Never"}
          </div>
          {last.fetched != null && (
            <div className="font-mono text-[10px] text-zinc-500 mt-1">
              {last.mode} · fetched {last.fetched} · +{last.created} · ~{last.updated}
            </div>
          )}
        </div>
      </div>

      {/* Promotion settings + Promote All (Phase 2) */}
      {settings && (
        <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-3">
          <h3 className="font-display text-lg uppercase tracking-tight">Promotion settings (Phase 2)</h3>
          <p className="font-mono text-[11px] text-zinc-500">
            Controls how approved SanMar styles map to your live customizable catalog. Existing manual products are NEVER overwritten — updates only refresh SanMar metadata (supplier_cost, inventory, synced_at).
          </p>
          <div className="grid md:grid-cols-3 gap-3">
            <label className="block">
              <span className="font-mono text-[10px] tracking-widest text-zinc-400">MARKUP MULTIPLIER</span>
              <input
                type="number" step="0.1" min="1.0" max="10.0"
                value={settings.markup_multiplier}
                onChange={(e) => setSettings({ ...settings, markup_multiplier: parseFloat(e.target.value) || 2 })}
                onBlur={(e) => saveSettings({ markup_multiplier: parseFloat(e.target.value) || 2 })}
                data-testid="sanmar-markup"
                className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-sm"
              />
              <span className="font-mono text-[10px] text-zinc-500">Retail = supplier_cost × multiplier (e.g. 2.0 = double SanMar price)</span>
            </label>
            <label className="block">
              <span className="font-mono text-[10px] tracking-widest text-zinc-400">DEFAULT CATEGORY</span>
              <input
                type="text"
                value={settings.default_category}
                onChange={(e) => setSettings({ ...settings, default_category: e.target.value })}
                onBlur={(e) => saveSettings({ default_category: e.target.value })}
                data-testid="sanmar-default-category"
                className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-sm"
              />
            </label>
            <label className="flex items-center gap-2 self-end mb-1 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.default_active}
                onChange={(e) => { setSettings({ ...settings, default_active: e.target.checked }); saveSettings({ default_active: e.target.checked }); }}
                data-testid="sanmar-default-active"
                className="accent-[var(--tc-red)]"
              />
              <span className="font-mono text-[11px] tracking-widest text-zinc-400">
                NEW PRODUCTS ACTIVE BY DEFAULT
              </span>
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={promoteAll}
              disabled={promotingAll || status.approved_styles_count === 0}
              data-testid="sanmar-promote-all-btn"
              className={`border text-white px-5 py-2 font-mono text-xs tracking-widest disabled:opacity-50 flex items-center gap-2 ${
                forceOverwrite
                  ? "bg-amber-800 border-amber-700 hover:bg-amber-700"
                  : "bg-emerald-800 border-emerald-700 hover:bg-emerald-700"
              }`}
            >
              <Upload size={14} className={promotingAll ? "animate-pulse" : ""} />
              {promotingAll
                ? "PROMOTING…"
                : `${forceOverwrite ? "FORCE OVERWRITE" : "PROMOTE"} ALL APPROVED (${status.approved_styles_count})`}
            </button>
            <label className="flex items-center gap-2 cursor-pointer" title="If ON, existing SanMar-linked products are REPLACED wholesale with fresh SanMar data (images, name, colors, sizes, description). Only the product id + created_at are preserved. OFF (default) only refreshes supplier_cost + inventory metadata on existing products.">
              <input
                type="checkbox"
                checked={forceOverwrite}
                onChange={(e) => setForceOverwrite(e.target.checked)}
                data-testid="sanmar-force-overwrite"
                className="accent-amber-500"
              />
              <span className={`font-mono text-[11px] tracking-widest ${forceOverwrite ? "text-amber-300" : "text-zinc-400"}`}>
                FORCE OVERWRITE EXISTING
              </span>
            </label>
            <span className="font-mono text-[10px] text-zinc-500">
              {savingSettings && "· saving settings…"}
            </span>
          </div>

          {/* iter66 — bulk category override for Promote All */}
          {taxonomy && (
            <div className="border border-[#222] bg-black/60 p-3 space-y-2" data-testid="bulk-category-override">
              <div className="font-mono text-[10px] tracking-widest text-zinc-500">
                CATEGORY OVERRIDE FOR PROMOTE ALL <span className="text-zinc-600">(leave empty → per-style smart classifier)</span>
              </div>
              <CategoryPicker
                taxonomy={taxonomy}
                value={bulkCategory}
                onChange={setBulkCategory}
                compact
                testidPrefix="bulk-cat"
              />
              {(bulkCategory.category || bulkCategory.subcategory) && (
                <button
                  type="button"
                  onClick={() => setBulkCategory({ category: "", subcategory: "" })}
                  data-testid="bulk-cat-clear"
                  className="font-mono text-[10px] text-zinc-400 hover:text-white underline"
                >
                  CLEAR — use per-style smart classifier instead
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* One-click reconcile — link live SanMar listings to bulk staging (iter61) */}
      <div className="border border-[var(--tc-red)] bg-red-950/10 p-5 space-y-3" data-testid="sanmar-reconcile-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-2xl">
            <h3 className="font-display text-lg uppercase tracking-tight flex items-center gap-2">
              <Zap size={16} className="text-[var(--tc-red)]" /> Sync live SanMar listings
            </h3>
            <p className="font-mono text-[11px] text-zinc-400 mt-1 leading-relaxed">
              One-click reconciliation for legacy manual imports. Links every <code className="text-white">source=sanmar</code> product on your live site with its Bulk API staging row, auto-approves the matched styles, scrapes back-view images, refreshes supplier cost + inventory, and activates the products. <span className="text-emerald-400">Safe update path only</span> — nothing is replaced wholesale.
            </p>
          </div>
          <button
            type="button"
            onClick={runReconcile}
            disabled={reconciling}
            data-testid="sanmar-reconcile-btn"
            className="bg-[var(--tc-red)] text-white px-5 py-3 font-mono text-xs tracking-widest hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-red-950/40"
          >
            <Zap size={14} className={reconciling ? "animate-pulse" : ""} />
            {reconciling ? "RECONCILING…" : "SYNC LIVE LISTINGS NOW"}
          </button>
        </div>

        {lastReconcile && (
          <div className={`border p-3 space-y-2 ${
            lastReconcile.phase === "done" ? (lastReconcile.ok ? "border-emerald-900 bg-emerald-950/20" : "border-amber-900 bg-amber-950/20")
            : lastReconcile.phase === "failed" ? "border-red-900 bg-red-950/20"
            : "border-blue-900 bg-blue-950/20"
          }`}>
            <div className="flex items-center gap-2 font-mono text-[11px] tracking-widest">
              {lastReconcile.phase === "done" && lastReconcile.ok && <CheckCircle2 size={13} className="text-emerald-400" />}
              {lastReconcile.phase === "done" && !lastReconcile.ok && <AlertCircle size={13} className="text-amber-400" />}
              {lastReconcile.phase === "failed" && <AlertCircle size={13} className="text-red-400" />}
              {lastReconcile.phase !== "done" && lastReconcile.phase !== "failed" && (
                <RefreshCw size={13} className="text-blue-400 animate-spin" />
              )}
              <span className={
                lastReconcile.phase === "done" ? (lastReconcile.ok ? "text-emerald-200" : "text-amber-200")
                : lastReconcile.phase === "failed" ? "text-red-200"
                : "text-blue-200"
              }>
                {lastReconcile.phase === "done"
                  ? `RECONCILE COMPLETE · ${fmt12hrTime(lastReconcile.finished_at)}`
                  : lastReconcile.phase === "failed"
                    ? `RECONCILE FAILED · ${lastReconcile.error || "unknown error"}`
                    : `RECONCILING · phase: ${(lastReconcile.phase || "").toUpperCase()}`}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="border border-[#222] bg-black p-2">
                <div className="font-mono text-[9px] text-zinc-500 tracking-widest">LIVE PRODUCTS</div>
                <div className="font-display text-lg text-white">{lastReconcile.live_products}</div>
              </div>
              <div className="border border-[#222] bg-black p-2">
                <div className="font-mono text-[9px] text-zinc-500 tracking-widest">STYLES MATCHED</div>
                <div className="font-display text-lg text-emerald-300">{lastReconcile.matched_styles?.length ?? 0}</div>
                {lastReconcile.backfilled_style_field > 0 && (
                  <div className="font-mono text-[9px] text-blue-300">+{lastReconcile.backfilled_style_field} field backfills</div>
                )}
              </div>
              <div className="border border-[#222] bg-black p-2">
                <div className="font-mono text-[9px] text-zinc-500 tracking-widest">VARIATIONS APPROVED</div>
                <div className="font-display text-lg text-emerald-300">{lastReconcile.approved_variations}</div>
              </div>
              <div className="border border-[#222] bg-black p-2">
                <div className="font-mono text-[9px] text-zinc-500 tracking-widest">PRODUCTS REFRESHED</div>
                <div className="font-display text-lg text-white">
                  {(lastReconcile.promote?.created ?? 0) + (lastReconcile.promote?.updated ?? 0) + (lastReconcile.promote?.overwritten ?? 0)}
                </div>
                {lastReconcile.activated_products > 0 && (
                  <div className="font-mono text-[9px] text-emerald-300">+{lastReconcile.activated_products} activated</div>
                )}
              </div>
            </div>
            {lastReconcile.scrape && (
              <div className="font-mono text-[10px] text-zinc-400">
                Back-image scrape: {lastReconcile.scrape.with_back ?? 0}/{lastReconcile.scrape.total_styles ?? 0} styles matched · {lastReconcile.scrape.rows_updated ?? 0} variation URLs saved
                {lastReconcile.scrape.fetch_failed > 0 && <span className="text-amber-400"> · {lastReconcile.scrape.fetch_failed} fetch fails</span>}
                {lastReconcile.scrape.processed !== undefined && lastReconcile.scrape.total_styles !== undefined && lastReconcile.scrape.processed < lastReconcile.scrape.total_styles && (
                  <span className="text-blue-300"> · in progress {lastReconcile.scrape.processed}/{lastReconcile.scrape.total_styles}</span>
                )}
              </div>
            )}
            {lastReconcile.unmatched_styles?.length > 0 && (
              <div className="font-mono text-[10px] text-amber-300">
                <span className="tracking-widest">UNMATCHED STYLES ({lastReconcile.unmatched_styles.length}):</span> {lastReconcile.unmatched_styles.slice(0, 8).join(", ")}
                {lastReconcile.unmatched_styles.length > 8 && ` +${lastReconcile.unmatched_styles.length - 8} more`}
              </div>
            )}
            {lastReconcile.no_style_could_infer?.length > 0 && (
              <div className="font-mono text-[10px] text-red-300">
                <span className="tracking-widest">COULD NOT INFER STYLE ({lastReconcile.no_style_could_infer.length}):</span> {lastReconcile.no_style_could_infer.slice(0, 5).map((p) => `${p.id}(sku=${p.sku})`).join(", ")}
              </div>
            )}
            {lastReconcile.promote?.errors?.length > 0 && (
              <div className="font-mono text-[10px] text-red-300">
                Promote errors: {lastReconcile.promote.errors.slice(0, 3).join(" · ")}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Back-view image scraper (iter60) */}
      {scrapeStatus && (
        <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-3" data-testid="sanmar-scraper-card">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-display text-lg uppercase tracking-tight flex items-center gap-2">
                <ImageIcon size={16} className="text-[var(--tc-red)]" /> Back-view images
              </h3>
              <p className="font-mono text-[11px] text-zinc-500 mt-1">
                SanMar&apos;s Bulk API only returns FRONT images. This scraper fetches back-view images from <code className="text-zinc-300">www.sanmarcanada.com</code> and stores them on each variation so the customizer can render the back side. Runs at ~4 pages/sec.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 min-w-[280px]">
              <div className="border border-[#222] bg-black px-3 py-2">
                <div className="font-mono text-[10px] tracking-widest text-zinc-500">STYLES</div>
                <div className="font-display text-xl text-zinc-100" data-testid="scrape-styles-coverage">
                  {scrapeStatus.styles_with_back} / {scrapeStatus.styles_total}
                </div>
                <div className="font-mono text-[10px] text-zinc-500">have back img</div>
              </div>
              <div className="border border-[#222] bg-black px-3 py-2">
                <div className="font-mono text-[10px] tracking-widest text-zinc-500">VARIATIONS</div>
                <div className="font-display text-xl text-emerald-300" data-testid="scrape-variations-coverage">
                  {scrapeStatus.variations_coverage_pct}%
                </div>
                <div className="font-mono text-[10px] text-zinc-500">
                  {scrapeStatus.variations_with_back} / {scrapeStatus.total_variations}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={scrapeOnlyApproved}
                onChange={(e) => setScrapeOnlyApproved(e.target.checked)}
                data-testid="scrape-only-approved"
                className="accent-[var(--tc-red)]"
              />
              <span className="font-mono text-[11px] tracking-widest text-zinc-400">
                ONLY APPROVED STYLES ({status.approved_styles_count})
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" title="If ON, re-scrape styles that already have back images (in case SanMar updated them).">
              <input
                type="checkbox"
                checked={scrapeForce}
                onChange={(e) => setScrapeForce(e.target.checked)}
                data-testid="scrape-force"
                className="accent-amber-500"
              />
              <span className={`font-mono text-[11px] tracking-widest ${scrapeForce ? "text-amber-300" : "text-zinc-400"}`}>
                FORCE REFRESH
              </span>
            </label>
            <button
              type="button"
              onClick={runScrapeBacks}
              disabled={scraping}
              data-testid="sanmar-scrape-btn"
              className="bg-[var(--tc-red)] text-white px-5 py-2 font-mono text-xs tracking-widest hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              <ImageIcon size={14} className={scraping ? "animate-pulse" : ""} />
              {scraping ? "SCRAPING…" : "SCRAPE BACK IMAGES"}
            </button>
          </div>

          {/* Live progress (during a run) */}
          {scrapeStatus.last_run?.total_styles > 0 && !scrapeStatus.last_run?.finished_at && (
            <div className="border border-blue-900 bg-blue-950/20 p-3 space-y-2">
              <div className="flex items-center justify-between font-mono text-[11px] text-blue-200">
                <span>SCRAPING · {scrapeStatus.last_run.processed}/{scrapeStatus.last_run.total_styles} styles</span>
                <span>{scrapeStatus.last_run.rows_updated} variations updated so far</span>
              </div>
              <div className="w-full bg-black border border-[#222] h-2 overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all"
                  style={{ width: `${Math.round((scrapeStatus.last_run.processed / scrapeStatus.last_run.total_styles) * 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Last completed run summary */}
          {scrapeStatus.last_run?.finished_at && (
            <div className={`border p-2 flex items-center gap-2 ${scrapeStatus.last_run.ok ? "border-emerald-900 bg-emerald-950/20" : "border-amber-900 bg-amber-950/20"}`}>
              {scrapeStatus.last_run.ok
                ? <CheckCircle2 size={13} className="text-emerald-400" />
                : <AlertCircle size={13} className="text-amber-400" />}
              <span className={`font-mono text-[11px] ${scrapeStatus.last_run.ok ? "text-emerald-200" : "text-amber-200"}`}>
                Last scrape · {scrapeStatus.last_run.with_back}/{scrapeStatus.last_run.total_styles} styles matched · {scrapeStatus.last_run.rows_updated} variations updated · {scrapeStatus.last_run.fetch_failed || 0} fetch fails · {scrapeStatus.last_run.no_back_images || 0} styles had no back images
              </span>
            </div>
          )}

          {scrapeStatus.last_run?.errors?.length > 0 && (
            <div className="border border-red-900 bg-red-950/20 p-3 space-y-1">
              <span className="font-mono text-[11px] tracking-widest text-red-200">SCRAPE ERRORS (first 5)</span>
              {scrapeStatus.last_run.errors.slice(0, 5).map((e, i) => (
                <div key={i} className="font-mono text-[11px] text-zinc-400">· {e}</div>
              ))}
            </div>
          )}

          <p className="font-mono text-[10px] text-zinc-600">
            After scraping, back images are automatically included in the next PROMOTE for new products.
            For existing products, promoting (without force overwrite) will backfill <code>back_color_images</code> if the product currently has none.
          </p>
        </div>
      )}

      {/* iter76 — SanMar PDF spec-sheet scraper. Fills product.spec_sheet
          (fabric, features, sizing table, detail images) so the customize
          page's SpecsSection renders below the canvas. */}
      <SpecScraperCard pw={pw} />

      {/* iter79d — DB-backed SanMar mode override. Lets the admin flip
          sandbox↔production without touching env vars or redeploying. */}
      <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-3" data-testid="sanmar-mode-override">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-display text-lg uppercase tracking-tight">Default Mode</h3>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-1">
              {modeInfo
                ? <>ACTIVE: <span className="text-white">{modeInfo.mode?.toUpperCase()}</span>{" "}
                    · SOURCE: <span className={modeInfo.source === "db" ? "text-amber-400" : "text-zinc-400"}>{modeInfo.source?.toUpperCase()}</span>{" "}
                    {modeInfo.source === "db" && <>· ENV FALLBACK: {modeInfo.env_mode?.toUpperCase()}</>}
                  </>
                : "LOADING…"}
            </div>
          </div>
          <div className="flex items-center gap-1" data-testid="sanmar-mode-toggle">
            {["sandbox", "production"].map((m) => (
              <button
                key={m}
                type="button"
                disabled={savingMode || modeInfo?.mode === m}
                onClick={async () => {
                  setSavingMode(true);
                  try {
                    const res = await adminSanmarSetMode(pw, m);
                    setModeInfo(res);
                    toast.success(`Mode set to ${m.toUpperCase()}`);
                  } catch { toast.error("Failed to update mode"); }
                  setSavingMode(false);
                }}
                data-testid={`sanmar-mode-btn-${m}`}
                className={`px-4 py-2 font-mono text-[10px] tracking-widest border ${
                  modeInfo?.mode === m
                    ? "border-[var(--tc-red)] bg-[var(--tc-red)]/10 text-white"
                    : "border-[#222] text-zinc-400 hover:border-zinc-500"
                } disabled:opacity-70`}
              >
                {m.toUpperCase()}
              </button>
            ))}
            {modeInfo?.source === "db" && (
              <button
                type="button"
                onClick={async () => {
                  setSavingMode(true);
                  try {
                    const res = await adminSanmarSetMode(pw, "unset");
                    setModeInfo(res);
                    toast.success(`Reverted to env (${res.mode.toUpperCase()})`);
                  } catch { toast.error("Failed to revert"); }
                  setSavingMode(false);
                }}
                data-testid="sanmar-mode-unset"
                className="ml-2 px-3 py-2 font-mono text-[10px] tracking-widest border border-[#333] text-zinc-500 hover:text-white hover:border-white disabled:opacity-70"
              >
                USE ENV
              </button>
            )}
          </div>
        </div>
        <div className="font-mono text-[10px] tracking-widest text-zinc-600">
          SANDBOX = unlimited test calls · PRODUCTION = live catalog, 1 sync per 24h.
          {modeInfo?.source === "db" && " Override persists across restarts and deploys."}
        </div>
      </div>

      {/* Sync controls */}
      <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-3">
        <h3 className="font-display text-lg uppercase tracking-tight">Sync now</h3>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] tracking-widest text-zinc-400">MODE</span>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              data-testid="sanmar-mode"
              className="bg-black border border-[#333] px-3 py-1.5 font-mono text-xs"
            >
              <option value="production">PRODUCTION (1/day, live inventory)</option>
              <option value="sandbox">SANDBOX (unlimited, for testing)</option>
            </select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              data-testid="sanmar-dry-run"
              className="accent-[var(--tc-red)]"
            />
            <span className="font-mono text-[11px] tracking-widest text-zinc-400">DRY RUN</span>
          </label>
          <button
            type="button"
            onClick={runSync}
            disabled={syncing || !configured || (mode === "production" && status.prod_rate_limited)}
            data-testid="sanmar-sync-btn"
            className="bg-[var(--tc-red)] text-white px-5 py-2 font-mono text-xs tracking-widest hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
            {syncing ? "SYNCING…" : "SYNC NOW"}
          </button>
        </div>
        {mode === "production" && status.prod_rate_limited && (
          <div className="border border-amber-900 bg-amber-950/20 p-2 flex items-center gap-2">
            <AlertCircle size={13} className="text-amber-400" />
            <span className="font-mono text-[11px] text-amber-200">
              Production rate-limited. Next available: {status.prod_next_available ? fmt12hrDateTime(status.prod_next_available) : "shortly"}
            </span>
          </div>
        )}
        {/* iter71/81 — live in-progress banner (phase set by _persist_progress
            after every chunk flush, or by the immediate "starting" upsert
            written before the SanMar HTTP fetch). Survives worker recycles
            + tab reloads. */}
        {["starting", "running", "started", "in_progress"].includes(last.phase) && !last.finished_at && (
          <div className="border border-blue-900 bg-blue-950/20 p-3 space-y-2" data-testid="sanmar-sync-progress">
            <div className="flex items-center justify-between font-mono text-[11px] tracking-widest">
              <span className="text-blue-200 flex items-center gap-2">
                <RefreshCw size={12} className="text-blue-400 animate-spin" />
                {(() => {
                  const done = (last.created ?? 0) + (last.updated ?? 0);
                  const total = last.total_products_reported || last.fetched || 0;
                  // Fetch phase = still pulling rows from SanMar API.
                  // Write phase = looping through DB inserts/updates (much longer for 20K SKUs).
                  const preFetch = ["starting", "running"].includes(last.phase) || (!last.fetched && done === 0);
                  const inWritePhase = last.fetched && done > 0 && done < total;
                  let label;
                  if (preFetch) {
                    label = "CONTACTING SANMAR API… (can take up to 90s)";
                  } else if (inWritePhase) {
                    label = `WRITING TO DB · ${done.toLocaleString()} / ${total.toLocaleString()} SKUs`;
                  } else {
                    label = "FETCHING FROM SANMAR…";
                  }
                  return <>SYNC IN PROGRESS · {label} · {(last.mode || "").toUpperCase()}{last.dry_run ? " · DRY" : ""}</>;
                })()}
              </span>
              <span className="text-zinc-400">
                {last.updated_at ? `updated ${fmt12hrTime(last.updated_at)}` : ""}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <div className="border border-[#222] bg-black p-2">
                <div className="font-mono text-[9px] text-zinc-500 tracking-widest">FETCHED</div>
                <div className="font-display text-lg text-white" data-testid="sanmar-progress-fetched">{last.fetched ?? 0}</div>
                {last.total_products_reported != null && (
                  <div className="font-mono text-[9px] text-zinc-500">of {last.total_products_reported}</div>
                )}
              </div>
              <div className="border border-[#222] bg-black p-2">
                <div className="font-mono text-[9px] text-zinc-500 tracking-widest">CREATED</div>
                <div className="font-display text-lg text-emerald-300">{last.created ?? 0}</div>
              </div>
              <div className="border border-[#222] bg-black p-2">
                <div className="font-mono text-[9px] text-zinc-500 tracking-widest">UPDATED</div>
                <div className="font-display text-lg text-zinc-100">{last.updated ?? 0}</div>
              </div>
              <div className="border border-[#222] bg-black p-2">
                <div className="font-mono text-[9px] text-zinc-500 tracking-widest">SKIPPED</div>
                <div className="font-display text-lg text-amber-300">{last.skipped ?? 0}</div>
              </div>
              <div className="border border-[#222] bg-black p-2">
                <div className="font-mono text-[9px] text-zinc-500 tracking-widest">STYLES</div>
                <div className="font-display text-lg text-zinc-100">{last.styles ?? "—"}</div>
              </div>
            </div>
            {last.total_products_reported > 0 && (
              <>
                <SpeedLine
                  done={(last.created || 0) + (last.updated || 0)}
                  total={last.total_products_reported}
                  active={(last.phase === "started" || last.phase === "in_progress") && !last.finished_at}
                />
                <div className="w-full bg-black border border-[#222] h-2 overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all"
                    style={{
                      width: `${Math.min(100, Math.round(((last.created + last.updated) / last.total_products_reported) * 100))}%`,
                    }}
                  />
                </div>
              </>
            )}
            <div className="font-mono text-[10px] text-zinc-500">
              Progress is persisted after every 1,000-row chunk so you can safely close this tab — the sync keeps running in the background.
            </div>
          </div>
        )}
        {last.errors && last.errors.length > 0 && last.finished_at && (
          <div className="border border-red-900 bg-red-950/20 p-3 space-y-1">
            <div className="flex items-center gap-2">
              <AlertCircle size={14} className="text-red-400" />
              <span className="font-mono text-[11px] tracking-widest text-red-200">LAST RUN ERRORS</span>
            </div>
            {last.errors.map((e, i) => (
              <div key={i} className="font-mono text-[11px] text-zinc-400 pl-5">· {e}</div>
            ))}
          </div>
        )}
        {last.ok && !last.errors?.length && last.finished_at && (
          <div className="border border-emerald-900 bg-emerald-950/20 p-2 flex items-center gap-2">
            <CheckCircle2 size={13} className="text-emerald-400" />
            <span className="font-mono text-[11px] text-emerald-200">
              Last {last.mode} sync OK · fetched {last.fetched} · {last.styles} styles
            </span>
          </div>
        )}
      </div>

      {/* Style browser + approval */}
      <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-3">
        <h3 className="font-display text-lg uppercase tracking-tight">Approve styles for the live catalog</h3>
        <p className="font-mono text-[11px] text-zinc-500">
          Check the styles you want to eventually promote to the live customizable catalog. Approval alone does not push to the live site — you&apos;ll trigger promotion in Phase 2. Existing manual ATC products are NEVER overwritten by SanMar.
        </p>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 bg-black border border-[#333] px-3 py-1.5">
            <Search size={13} className="text-zinc-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search style / product name"
              data-testid="sanmar-search"
              className="bg-transparent outline-none text-sm font-body"
            />
          </div>
          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            data-testid="sanmar-brand-filter"
            className="bg-black border border-[#333] px-3 py-1.5 font-mono text-xs"
          >
            <option value="">ALL BRANDS</option>
            {BRAND_QUICK_PICKS.map((b) => (<option key={b} value={b}>{b}</option>))}
          </select>
          <select
            value={approvedFilter}
            onChange={(e) => setApprovedFilter(e.target.value)}
            data-testid="sanmar-approved-filter"
            className="bg-black border border-[#333] px-3 py-1.5 font-mono text-xs"
          >
            <option value="">ALL APPROVAL STATES</option>
            <option value="true">APPROVED ONLY</option>
            <option value="false">NOT APPROVED</option>
          </select>
          <span className="font-mono text-[10px] text-zinc-500 ml-auto">
            {styles.length} styles shown
          </span>
        </div>

        <div className="border border-[#222] max-h-[540px] overflow-y-auto" data-testid="sanmar-styles-list">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-black border-b border-[#222]">
              <tr>
                <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest text-zinc-500">STYLE</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest text-zinc-500">PRODUCT</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest text-zinc-500">BRAND</th>
                <th className="text-right px-3 py-2 font-mono text-[10px] tracking-widest text-zinc-500">VARS</th>
                <th className="text-right px-3 py-2 font-mono text-[10px] tracking-widest text-zinc-500">PRICE</th>
                <th className="text-right px-3 py-2 font-mono text-[10px] tracking-widest text-zinc-500">STOCK</th>
                <th className="text-center px-3 py-2 font-mono text-[10px] tracking-widest text-zinc-500">APPROVE</th>
                <th className="text-center px-3 py-2 font-mono text-[10px] tracking-widest text-zinc-500">PROMOTE</th>
              </tr>
            </thead>
            <tbody>
              {styles.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 font-mono text-xs text-zinc-500">No styles match these filters.</td></tr>
              )}
              {styles.map((s) => {
                const approved = s.approved_state === "all";
                const partial = s.approved_state === "partial";
                return (
                  <tr key={s.style} className="border-b border-[#111] hover:bg-[#0a0a0a]" data-testid={`sanmar-style-${s.style}`}>
                    <td className="px-3 py-2 font-mono text-xs text-emerald-300">{s.style}</td>
                    <td className="px-3 py-2 text-zinc-100">
                      <div className="flex items-center gap-2">
                        {s.image && (
                          <img src={s.image} alt="" referrerPolicy="no-referrer" className="w-8 h-8 object-cover border border-[#222]" loading="lazy" />
                        )}
                        <span className="truncate max-w-[380px]" dangerouslySetInnerHTML={{ __html: s.product_name || "?" }} />
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-zinc-400">{s.brand}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-zinc-300">{s.variations}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-zinc-300">
                      {s.min_price != null ? `$${s.min_price.toFixed(2)}` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-zinc-300">{s.total_qty}</td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => toggleApproval(s.style, approved)}
                        data-testid={`sanmar-toggle-${s.style}`}
                        className={`px-3 py-1 font-mono text-[10px] tracking-widest border transition-colors ${
                          approved ? "border-emerald-700 bg-emerald-900/40 text-emerald-200 hover:bg-emerald-900/60"
                          : partial ? "border-amber-700 bg-amber-900/40 text-amber-200 hover:bg-amber-900/60"
                          : "border-[#333] text-zinc-400 hover:border-emerald-700 hover:text-emerald-300"
                        }`}
                      >
                        {approved ? <><Check size={11} className="inline" /> APPROVED</> : partial ? "PARTIAL" : "APPROVE"}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => openPreview(s.style)}
                          data-testid={`sanmar-preview-${s.style}`}
                          disabled={!approved && !partial}
                          title={approved || partial ? "Preview how this style will appear in the live catalog" : "Approve first"}
                          className="border border-[#333] text-zinc-300 hover:border-zinc-500 disabled:opacity-30 disabled:cursor-not-allowed px-2 py-1 font-mono text-[10px] tracking-widest inline-flex items-center gap-1"
                        >
                          <Eye size={11} /> PREVIEW
                        </button>
                        <button
                          type="button"
                          onClick={() => promoteOne(s.style)}
                          data-testid={`sanmar-promote-${s.style}`}
                          disabled={(!approved && !partial) || promotingStyle === s.style}
                          title={approved || partial ? "Promote this style to the live catalog" : "Approve first"}
                          className="border border-emerald-800 bg-emerald-900/30 text-emerald-200 hover:bg-emerald-900/60 disabled:opacity-30 disabled:cursor-not-allowed px-2 py-1 font-mono text-[10px] tracking-widest inline-flex items-center gap-1"
                        >
                          <Upload size={11} className={promotingStyle === s.style ? "animate-pulse" : ""} />
                          {promotingStyle === s.style ? "…" : "PROMOTE"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent runs log */}
      <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-display text-lg uppercase tracking-tight">Recent syncs</h3>
          <button
            type="button"
            data-testid="sanmar-clear-errors"
            onClick={async () => {
              if (!window.confirm("Delete all FAILED SanMar sync log rows? Successful runs stay.")) return;
              try {
                const r = await adminSanmarClearErrors(pw);
                const parts = [`removed ${r.failed_removed} failed`];
                if (r.zombies_removed) parts.push(`${r.zombies_removed} interrupted`);
                toast.success(`Sync log cleared — ${parts.join(", ")}`);
                setRuns(await adminSanmarRuns(pw, 20));
                await refreshStatus();
              } catch (e) {
                toast.error(`Clear failed: ${e.response?.data?.detail || e.message}`);
              }
            }}
            className="border border-red-900 hover:border-red-500 text-red-400 hover:text-red-200 px-3 py-1.5 font-mono text-[10px] tracking-widest inline-flex items-center gap-1.5"
          >
            <X size={11} /> CLEAR FAILED RUNS
          </button>
        </div>
        {runs.length === 0 && <div className="font-mono text-xs text-zinc-500">No runs yet.</div>}
        {runs.map((r) => {
          // iter76c/81 — a running sync briefly persists as `ok:false, phase:running/starting/in_progress`
          // while it awaits SanMar's response. Show a distinct amber "RUNNING"
          // pill instead of the previous red "FAIL" so operators don't panic
          // during the ~90s in-flight window. Terminal error phases (failed,
          // error) still render red.
          const running = ["starting", "running", "in_progress", "started"].includes(r.phase);
          return (
          <div key={`${r.run_id}-${r.started_at}`} className="border border-[#222] p-3 font-mono text-[11px]">
            <div className="flex justify-between mb-1">
              <span className={running ? "text-amber-300" : (r.ok ? "text-emerald-300" : "text-red-300")}>
                {running ? "RUNNING" : (r.ok ? "OK" : "FAIL")} · {r.mode?.toUpperCase()} {r.dry_run && "· DRY"}
              </span>
              <span className="text-zinc-500">{fmt12hrDateTimeShort(r.started_at)}</span>
            </div>
            <div className="text-zinc-400">
              fetched {r.fetched} · {r.styles} styles · +{r.created} · ~{r.updated} · skip {r.skipped}
            </div>
            {r.errors?.length > 0 && (
              <div className="text-red-300 mt-1">{r.errors.slice(0, 2).join(" · ")}</div>
            )}
          </div>
          );
        })}
      </div>

      {/* Preview modal (Phase 2) */}
      {previewStyle && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closePreview}
          data-testid="sanmar-preview-modal"
        >
          <div
            className="bg-[#0a0a0a] border border-[#333] max-w-4xl w-full max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-[#222] p-4 flex items-center justify-between sticky top-0 bg-[#0a0a0a]">
              <h3 className="font-display text-xl uppercase tracking-tight flex items-center gap-2">
                <Eye size={18} className="text-[var(--tc-red)]" /> Preview · {previewStyle}
              </h3>
              <button
                type="button"
                onClick={closePreview}
                data-testid="sanmar-preview-close"
                className="text-zinc-500 hover:text-white p-1"
                aria-label="Close preview"
              >
                <X size={18} />
              </button>
            </div>

            {previewLoading ? (
              <div className="p-8 text-center font-mono text-xs text-zinc-500">Loading preview…</div>
            ) : !previewData?.ok ? (
              <div className="p-8 font-mono text-xs text-red-300">
                {previewData?.error || "Preview failed"}
              </div>
            ) : (
              <div className="p-5 space-y-5">
                {/* Action banner */}
                <div className={`border p-3 flex items-center gap-2 ${previewData.action === "create" ? "border-emerald-800 bg-emerald-950/30" : "border-amber-800 bg-amber-950/30"}`}>
                  {previewData.action === "create"
                    ? <><CheckCircle2 size={15} className="text-emerald-400" /><span className="font-mono text-xs text-emerald-200">WILL CREATE new product</span></>
                    : <><AlertCircle size={15} className="text-amber-400" /><span className="font-mono text-xs text-amber-200">WILL UPDATE existing product · only supplier_cost + inventory refreshed · slug/images/pricing/description preserved</span></>}
                </div>

                {/* Product card preview */}
                <div className="grid md:grid-cols-2 gap-5">
                  {/* Hero image */}
                  <div className="border border-[#222] bg-black p-2">
                    {previewData.generated.image ? (
                      <img
                        src={previewData.generated.image}
                        alt=""
                        referrerPolicy="no-referrer"
                        className="w-full aspect-square object-contain bg-white"
                      />
                    ) : (
                      <div className="w-full aspect-square flex items-center justify-center text-zinc-600 font-mono text-xs">no image</div>
                    )}
                  </div>

                  {/* Details grid */}
                  <div className="space-y-3">
                    <div>
                      <div className="font-mono text-[10px] tracking-widest text-zinc-500">NAME</div>
                      <div className="text-zinc-100 text-sm" data-testid="preview-name" dangerouslySetInnerHTML={{ __html: previewData.generated.name || "?" }} />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <div className="font-mono text-[10px] tracking-widest text-zinc-500">SLUG</div>
                        <div className="font-mono text-xs text-zinc-100" data-testid="preview-slug">/customize/{previewData.generated.slug}</div>
                      </div>
                      <div>
                        <div className="font-mono text-[10px] tracking-widest text-zinc-500">SKU</div>
                        <div className="font-mono text-xs text-zinc-100">{previewData.generated.sku}</div>
                      </div>
                      <div>
                        <div className="font-mono text-[10px] tracking-widest text-zinc-500">CATEGORY</div>
                        <div className="font-mono text-xs text-zinc-100">{previewData.generated.category}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <div className="font-mono text-[10px] tracking-widest text-zinc-500">SUPPLIER COST</div>
                        <div className="font-mono text-sm text-zinc-100">${previewData.generated.supplier_cost?.toFixed(2)}</div>
                      </div>
                      <div>
                        <div className="font-mono text-[10px] tracking-widest text-zinc-500">RETAIL (×{previewData.settings.markup_multiplier})</div>
                        <div className="font-mono text-sm text-emerald-300" data-testid="preview-retail">${previewData.generated.base_price?.toFixed(2)}</div>
                      </div>
                      <div>
                        <div className="font-mono text-[10px] tracking-widest text-zinc-500">INVENTORY</div>
                        <div className="font-mono text-sm text-zinc-100">{previewData.generated.sanmar_inventory}</div>
                      </div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] tracking-widest text-zinc-500">
                        SIZES ({previewData.generated.sizes.length})
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {previewData.generated.sizes.map((sz) => (
                          <span key={sz} className="border border-[#333] px-2 py-0.5 font-mono text-[10px] text-zinc-200">{sz}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] tracking-widest text-zinc-500">PRINT LOCATIONS</div>
                      <div className="flex gap-1 mt-1">
                        {(previewData.generated.customization_config?.print_locations || []).map((loc) => (
                          <span key={loc} className="border border-emerald-800 bg-emerald-900/30 text-emerald-200 px-2 py-0.5 font-mono text-[10px] uppercase">{loc}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] tracking-widest text-zinc-500">DEFAULT STATE</div>
                      <div className={`font-mono text-xs ${previewData.generated.active ? "text-emerald-300" : "text-amber-300"}`}>
                        {previewData.generated.active ? "ACTIVE (live immediately)" : "DRAFT (active=false, flip in product editor)"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Color swatches */}
                <div>
                  <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2">
                    COLOR SWATCHES ({Object.keys(previewData.generated.color_images || {}).length})
                  </div>
                  <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2" data-testid="preview-colors">
                    {Object.entries(previewData.generated.color_images || {}).map(([color, img]) => (
                      <div key={color} className="border border-[#222] bg-black p-1">
                        <img
                          src={img}
                          alt={color}
                          loading="lazy"
                          width="120"
                          height="120"
                          referrerPolicy="no-referrer"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                          className="w-full aspect-square object-contain bg-white mb-1"
                        />
                        <div className="font-mono text-[10px] text-zinc-400 text-center truncate">{color}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Existing product warning */}
                {previewData.existing && (
                  <div className="border border-amber-800 bg-amber-950/20 p-3 space-y-1">
                    <div className="font-mono text-[11px] tracking-widest text-amber-200">
                      EXISTING PRODUCT — WILL BE PRESERVED
                    </div>
                    <div className="font-mono text-[11px] text-zinc-400">
                      · id: {previewData.existing.id}<br />
                      · slug: /customize/{previewData.existing.slug}<br />
                      · name: {previewData.existing.name}<br />
                      · base_price: ${previewData.existing.base_price}<br />
                      · active: {String(previewData.existing.active)}<br />
                      · source: {previewData.existing.source || "manual"}
                    </div>
                    <div className="font-mono text-[11px] text-amber-300 mt-2">
                      Promoting will ONLY refresh: supplier_cost, sanmar_inventory, sanmar_variation_count, sanmar_synced_at. Everything else stays.
                    </div>
                  </div>
                )}

                {/* iter66 — per-style category picker */}
                {taxonomy && (
                  <div className="border border-[#222] bg-black/60 p-3 space-y-2" data-testid="preview-category-picker">
                    <div className="font-mono text-[10px] tracking-widest text-zinc-500">CATEGORY FOR THIS STYLE</div>
                    <CategoryPicker
                      taxonomy={taxonomy}
                      inferred={previewInferred}
                      value={previewCategory}
                      onChange={setPreviewCategory}
                      compact
                      testidPrefix="preview-cat"
                    />
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex items-center justify-between gap-3 border-t border-[#222] pt-4 flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer" title="If ON, existing product is REPLACED wholesale with fresh SanMar data (images, name, colors, sizes, description). Only id + created_at are preserved.">
                    <input
                      type="checkbox"
                      checked={forceOverwrite}
                      onChange={(e) => setForceOverwrite(e.target.checked)}
                      data-testid="sanmar-preview-force-overwrite"
                      className="accent-amber-500"
                    />
                    <span className={`font-mono text-[11px] tracking-widest ${forceOverwrite ? "text-amber-300" : "text-zinc-400"}`}>
                      FORCE OVERWRITE EXISTING
                    </span>
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={closePreview}
                      className="border border-[#333] text-zinc-300 hover:border-zinc-500 px-4 py-2 font-mono text-xs tracking-widest"
                    >
                      CANCEL
                    </button>
                    <button
                      type="button"
                      onClick={() => promoteOne(previewStyle)}
                      disabled={promotingStyle === previewStyle}
                      data-testid="sanmar-preview-promote-btn"
                      className={`border text-white px-5 py-2 font-mono text-xs tracking-widest disabled:opacity-50 flex items-center gap-2 ${
                        forceOverwrite
                          ? "bg-amber-800 border-amber-700 hover:bg-amber-700"
                          : "bg-emerald-800 border-emerald-700 hover:bg-emerald-700"
                      }`}
                    >
                      <Upload size={13} className={promotingStyle === previewStyle ? "animate-pulse" : ""} />
                      {promotingStyle === previewStyle
                        ? "PROMOTING…"
                        : previewData.action === "create"
                          ? "CREATE PRODUCT"
                          : forceOverwrite ? "OVERWRITE PRODUCT" : "UPDATE METADATA"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * iter76 — SanMar spec-sheet scraper card.
 *
 * Fetches the official product PDF from media.sanmarcanada.com/pdfs/,
 * parses structured spec data (fabric, features, sizing chart, detail
 * illustrations) and stores it on `product.spec_sheet`. The frontend's
 * SpecsSection component then renders those fields below the customize
 * canvas in the site's TC red/black aesthetic.
 *
 * UI mirrors the back-image scraper card above: coverage stat tile,
 * "SCRAPE ALL MISSING" button, force-overwrite checkbox, live progress
 * bar during running batches.
 */
function SpecScraperCard({ pw }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [force, setForce] = useState(false);
  const [pollTimer, setPollTimer] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setStatus(await adminSanmarScrapeSpecsStatus(pw));
    } catch (_e) { /* ignore */ }
  }, [pw]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => () => pollTimer && clearInterval(pollTimer), [pollTimer]);

  const running = busy || status?.last_run?.phase === "running";
  useEffect(() => {
    if (!running) return;
    const id = setInterval(refresh, 3000);
    setPollTimer(id);
    return () => clearInterval(id);
  }, [running, refresh]);

  useEffect(() => {
    if (!busy) return;
    const phase = status?.last_run?.phase;
    if (phase === "done" || phase === "error") {
      setBusy(false);
      const lr = status?.last_run || {};
      if (phase === "error") {
        toast.error(`Spec scrape failed: ${lr.error || "unknown"}`);
      } else {
        toast.success(
          `Scraped ${lr.scraped} products · ${lr.success} succeeded · ${lr.failed} had no PDF (${lr.elapsed_s}s)`
        );
      }
    }
  }, [busy, status?.last_run]);

  const runBatch = async () => {
    setBusy(true);
    try {
      const r = await adminSanmarScrapeSpecs(pw, { force });
      if (r.queued) {
        toast.info(`Queued spec scrape ${r.run_id}. Live progress below.`, { duration: 4000 });
      }
      await refresh();
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || e?.message || "Failed to queue scrape");
    }
  };

  if (!status) return null;
  const lr = status.last_run || {};
  const missing = status.missing_count || 0;

  return (
    <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-3" data-testid="sanmar-spec-scraper-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg uppercase tracking-tight flex items-center gap-2">
            <FileText size={16} className="text-[var(--tc-red)]" /> Spec sheets &amp; sizing charts
          </h3>
          <p className="font-mono text-[11px] text-zinc-500 mt-1">
            Downloads the official SanMar Canada PDF from{" "}
            <code className="text-zinc-300">media.sanmarcanada.com/pdfs/</code>{" "}
            and extracts fabric, feature bullets, garment measurements table, and construction illustrations.
            The customize page&apos;s &quot;Specs &amp; Sizing&quot; section renders these fields automatically.
            Newly-promoted products auto-scrape; a nightly cron at 05:23 UTC reconciles anything missing.
          </p>
        </div>
        <div className="border border-[#222] bg-black px-3 py-2 min-w-[180px]">
          <div className="font-mono text-[10px] tracking-widest text-zinc-500">MISSING SPECS</div>
          <div className={`font-display text-2xl ${missing === 0 ? "text-emerald-300" : "text-amber-300"}`} data-testid="specs-missing-count">
            {missing}
          </div>
          <div className="font-mono text-[10px] text-zinc-500">customize products</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={force}
            onChange={(e) => setForce(e.target.checked)}
            data-testid="specs-force-toggle"
            className="accent-[var(--tc-red)]"
            disabled={running}
          />
          <span className="font-mono text-[11px] tracking-widest text-zinc-400">
            FORCE (re-scrape products that already have specs)
          </span>
        </label>
        <button
          type="button"
          onClick={runBatch}
          disabled={running}
          data-testid="specs-scrape-btn"
          className="bg-[var(--tc-red)] text-white px-5 py-2 font-mono text-xs tracking-widest hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
        >
          <RefreshCw size={14} className={running ? "animate-spin" : ""} />
          {running ? "SCRAPING…" : (force ? "RE-SCRAPE ALL" : "SCRAPE ALL MISSING")}
        </button>
      </div>

      {/* Live progress — polls every 3s while phase="running" */}
      {lr.phase === "running" && (
        <div className="border border-emerald-900 bg-emerald-950/20 p-3" data-testid="specs-scrape-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[11px] tracking-widest text-emerald-200">
              LIVE · SCRAPED {lr.scraped || 0}  ·  SUCCESS {lr.success || 0}  ·  FAILED {lr.failed || 0}
            </span>
            <span className="font-mono text-[10px] text-emerald-300">RUNNING</span>
          </div>
          <div className="font-mono text-[10px] text-zinc-500">
            SanMar doesn&apos;t publish PDFs for every style — failed count is expected for a fraction of products.
          </div>
        </div>
      )}

      {lr.phase === "done" && (
        <div className="border border-emerald-900 bg-emerald-950/20 p-2 flex items-center gap-2">
          <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
          <span className="font-mono text-[11px] text-emerald-200">
            Last scrape · {lr.success} succeeded · {lr.failed} had no PDF · {lr.elapsed_s}s
          </span>
        </div>
      )}
      {lr.phase === "error" && (
        <div className="border border-red-900 bg-red-950/20 p-2 flex items-center gap-2">
          <AlertCircle size={14} className="text-red-400 shrink-0" />
          <span className="font-mono text-[11px] text-red-200">
            Last scrape errored: {lr.error}
          </span>
        </div>
      )}
    </div>
  );
}


/** Small reusable speed + ETA line for sync progress cards (iter79e). */
function SpeedLine({ done, total, active }) {
  const { speed, etaStr, ready } = useSyncSpeed({ done, total, active });
  if (!active || !total) return null;
  return (
    <div className="flex items-center justify-between font-mono text-[10px] tracking-widest text-zinc-500">
      <span>
        {ready ? <>~{speed.toLocaleString()} SKUs/sec</> : "measuring speed…"}
      </span>
      <span className="text-zinc-400">
        ETA {ready ? etaStr : "…"}
      </span>
    </div>
  );
}
